import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PreviewFilterState {
  // Channel filters
  showDescription: boolean;
  showPrice: boolean;
  showPlans: boolean;
  showNumbers: boolean; // Channel numbers are required but we'll include this for completeness
  showInAllCategories: boolean; // New option for multi-category display
  showLocalChannelsCategory: boolean; // Option for showing local channels in their own category
  hideSerialNumbers: boolean; // Option to hide serial numbers for sequential providers
  sortOrder: 'asc' | 'desc'; // Sorting order for channels
  setShowDescription: (show: boolean) => void;
  setShowPrice: (show: boolean) => void;
  setShowPlans: (show: boolean) => void;
  setShowNumbers: (show: boolean) => void;
  setShowInAllCategories: (show: boolean) => void; // New setter
  setShowLocalChannelsCategory: (show: boolean) => void; // Setter for local channels category
  setHideSerialNumbers: (hide: boolean) => void; // Setter for hiding serial numbers
  setSortOrder: (order: 'asc' | 'desc') => void; // Setter for sort order
  
  // Plan filters
  showPlanDescription: boolean;
  showPlanMonthlyPrice: boolean;
  showPlanYearlyPrice: boolean;
  setShowPlanDescription: (show: boolean) => void;
  setShowPlanMonthlyPrice: (show: boolean) => void;
  setShowPlanYearlyPrice: (show: boolean) => void;
  
  // Category filters
  showCategoryDescription: boolean;
  showCategoryPrice: boolean;
  setShowCategoryDescription: (show: boolean) => void;
  setShowCategoryPrice: (show: boolean) => void;

  // Package filters
  showPackages: boolean;
  showPackageDescription: boolean;
  showPackageMonthlyPrice: boolean;
  showPackageYearlyPrice: boolean;
  showPackageType: boolean;
  setShowPackages: (show: boolean) => void;
  setShowPackageDescription: (show: boolean) => void;
  setShowPackageMonthlyPrice: (show: boolean) => void;
  setShowPackageYearlyPrice: (show: boolean) => void;
  setShowPackageType: (show: boolean) => void;

  // Tag filters
  showTags: boolean;
  showTagDescription: boolean;
  showTagChannelCount: boolean;
  setShowTags: (show: boolean) => void;
  setShowTagDescription: (show: boolean) => void;
  setShowTagChannelCount: (show: boolean) => void;

  resetFilters: () => void;
}

export const usePreviewFilterStore = create<PreviewFilterState>()(
  persist(
    (set) => ({
      // Channel filters (existing)
      showDescription: true,
      showPrice: true,
      showPlans: true,
      showNumbers: true,
      showInAllCategories: false, // Default to showing channels only in their default category
      showLocalChannelsCategory: false, // Default to not showing local channels as separate category
      hideSerialNumbers: false, // Default to showing serial numbers
      sortOrder: 'asc' as 'asc' | 'desc', // Default to ascending order
      setShowDescription: (show) => set({ showDescription: show }),
      setShowPrice: (show) => set({ showPrice: show }),
      setShowPlans: (show) => set({ showPlans: show }),
      setShowNumbers: (show) => set({ showNumbers: show }),
      setShowInAllCategories: (show) => set({ showInAllCategories: show }),
      setShowLocalChannelsCategory: (show) => set({ showLocalChannelsCategory: show }),
      setHideSerialNumbers: (hide) => set({ hideSerialNumbers: hide }),
      setSortOrder: (order) => set({ sortOrder: order }),
      
      // Plan filters (new)
      showPlanDescription: true,
      showPlanMonthlyPrice: true,
      showPlanYearlyPrice: true,
      setShowPlanDescription: (show) => set({ showPlanDescription: show }),
      setShowPlanMonthlyPrice: (show) => set({ showPlanMonthlyPrice: show }),
      setShowPlanYearlyPrice: (show) => set({ showPlanYearlyPrice: show }),
      
      // Category filters (new)
      showCategoryDescription: true,
      showCategoryPrice: true,
      setShowCategoryDescription: (show) => set({ showCategoryDescription: show }),
      setShowCategoryPrice: (show) => set({ showCategoryPrice: show }),

      // Package filters
      showPackages: true,
      showPackageDescription: true,
      showPackageMonthlyPrice: true,
      showPackageYearlyPrice: true,
      showPackageType: true,
      setShowPackages: (show) => set({ showPackages: show }),
      setShowPackageDescription: (show) => set({ showPackageDescription: show }),
      setShowPackageMonthlyPrice: (show) => set({ showPackageMonthlyPrice: show }),
      setShowPackageYearlyPrice: (show) => set({ showPackageYearlyPrice: show }),
      setShowPackageType: (show) => set({ showPackageType: show }),

      // Tag filters
      showTags: true,
      showTagDescription: true,
      showTagChannelCount: true,
      setShowTags: (show) => set({ showTags: show }),
      setShowTagDescription: (show) => set({ showTagDescription: show }),
      setShowTagChannelCount: (show) => set({ showTagChannelCount: show }),

      resetFilters: () => set({
        // Reset channel filters
        showDescription: true,
        showPrice: true,
        showPlans: true,
        showNumbers: true,
        showInAllCategories: false, // Reset to default behavior
        showLocalChannelsCategory: false, // Reset local channels category
        hideSerialNumbers: false, // Reset to showing serial numbers
        sortOrder: 'asc' as 'asc' | 'desc', // Reset to ascending order

        // Reset plan filters
        showPlanDescription: true,
        showPlanMonthlyPrice: true,
        showPlanYearlyPrice: true,

        // Reset category filters
        showCategoryDescription: true,
        showCategoryPrice: true,

        // Reset package filters
        showPackages: true,
        showPackageDescription: true,
        showPackageMonthlyPrice: true,
        showPackageYearlyPrice: true,
        showPackageType: true,

        // Reset tag filters
        showTags: true,
        showTagDescription: true,
        showTagChannelCount: true,
      }),
    }),
    {
      name: 'preview-filter-storage',
    }
  )
); 